import React, { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { Heart, Award, Star, Calendar } from "lucide-react";

const TYPE_META = {
  spotlight: { icon: Star, color: "from-amber-400 to-orange-500" },
  win: { icon: Award, color: "from-emerald-400 to-emerald-600" },
  milestone: { icon: Star, color: "from-fuchsia-500 to-pink-500" },
  birthday: { icon: Heart, color: "from-pink-400 to-rose-500" },
  retirement: { icon: Award, color: "from-blue-500 to-indigo-500" },
  new_hire: { icon: Star, color: "from-cyan-500 to-blue-500" },
  wellness: { icon: Heart, color: "from-violet-500 to-purple-500" },
};

export default function Community() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/community").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  const spotlights = posts.filter((p) => p.post_type === "spotlight");
  const wins = posts.filter((p) => p.post_type === "win");
  const board = posts.filter((p) => !["spotlight", "win", "wellness"].includes(p.post_type));
  const wellness = t("community.wellnessItems");

  return (
    <div data-testid="community-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("community.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("community.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("community.spotlights")}</h2>
        <div className="h-1 w-12 cupe-gradient rounded-full mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spotlights.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition">
              {p.image_url && <div className="aspect-video bg-slate-100 overflow-hidden"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="p-5">
                <div className="font-display font-bold text-lg text-slate-900">{lang === "fr" ? p.title_fr : p.title_en}</div>
                <p className="text-slate-600 mt-2 text-sm">{lang === "fr" ? p.content_fr : p.content_en}</p>
              </div>
            </div>
          ))}
          {spotlights.length === 0 && <p className="text-slate-400">-</p>}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("community.wins")}</h2>
          <div className="h-1 w-12 cupe-gradient rounded-full mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {wins.map((p) => (
              <div key={p.id} className="bg-white border-l-4 border-emerald-500 rounded-xl p-5 flex gap-4">
                <Award className="text-emerald-500 flex-shrink-0" size={28} />
                <div>
                  <div className="font-display font-bold text-slate-900">{lang === "fr" ? p.title_fr : p.title_en}</div>
                  <p className="text-slate-600 text-sm mt-1">{lang === "fr" ? p.content_fr : p.content_en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("community.board")}</h2>
        <div className="h-1 w-12 cupe-gradient rounded-full mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {board.map((p) => {
            const meta = TYPE_META[p.post_type] || { icon: Star, color: "from-pink-500 to-rose-500" };
            const Icon = meta.icon;
            return (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center mb-3`}><Icon className="text-white" size={20} /></div>
                <div className="text-xs uppercase tracking-widest text-slate-400">{p.post_type.replace("_", " ")}</div>
                <div className="font-display font-semibold text-slate-900 mt-1">{lang === "fr" ? p.title_fr : p.title_en}</div>
                {p.content_en && <p className="text-slate-600 text-sm mt-2">{lang === "fr" ? p.content_fr : p.content_en}</p>}
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-gradient-to-br from-pink-50 to-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{t("community.wellness")}</h2>
          <div className="h-1 w-12 cupe-gradient rounded-full mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {Array.isArray(wellness) && wellness.map((w, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6">
                <Heart className="text-[#d81b7a] mb-3" size={24} />
                <div className="font-display font-bold text-slate-900">{w.t}</div>
                <p className="text-slate-600 text-sm mt-2">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
